/**
 * Meta标签管理工具
 * 用于动态更新页面的meta标签，特别是微信分享相关的标签
 */

/**
 * 更新页面的meta标签
 * @param {Object} metaData meta标签数据
 * @param {string} metaData.title 页面标题
 * @param {string} metaData.description 页面描述
 * @param {string} metaData.image 分享图片URL
 * @param {string} metaData.url 页面URL
 * @param {string} metaData.type 内容类型
 */
export function updateMetaTags(metaData) {
  // 确保图片URL是绝对路径
  const ensureAbsoluteUrl = (url) => {
    if (!url) return 'https://mall.sainbainu.com/static/images/sharelogo.png'
    if (url.startsWith('http')) return url
    if (url.startsWith('/')) return `${window.location.origin}${url}`
    return `${window.location.origin}/${url}`
  }
  
  // 处理URL，保持原始参数不变（包括tm参数）
  const processShareLink = (link) => {
    try {
      const url = new URL(link || window.location.href);
      // 保留所有参数，包括tm参数，只移除其他动态参数
      ['t', 'timestamp', '_t', '_timestamp', 'time', 'v'].forEach(param => {
        url.searchParams.delete(param);
      });
      // 确保包含scene=1
      url.searchParams.set('scene', '1');
      return url.toString();
    } catch (e) {
      console.warn('URL处理失败:', e);
      const baseUrl = link || window.location.href;
      // 简单字符串处理，保留原始参数
      let cleanUrl = baseUrl.split('#')[0];
      // 移除其他动态参数，但保留tm参数
      cleanUrl = cleanUrl.replace(/[&?](t|timestamp|_t|_timestamp|time|v)=\d+/g, '');
      if (cleanUrl.includes('?')) {
        return `${cleanUrl}&scene=1`;
      } else {
        return `${cleanUrl}?scene=1`;
      }
    }
  };
  
  const {
    title = '蒙古丽人商城',
    description = '专注于蒙古服饰设计、开发、推广-',
    image = 'https://mall.sainbainu.com/static/images/sharelogo.png',
    url = window.location.href.split('#')[0], // 移除hash部分
    type = 'website'
  } = metaData
  
  // 确保描述字段正确处理
  const desc = metaData.desc || metaData.description || description
  
  // 确保图片URL是绝对路径
  const absoluteImageUrl = ensureAbsoluteUrl(image)
  
  // 确保URL包含scene=1参数
  const processedUrl = processShareLink(url)

  // 更新页面标题
  document.title = title
  
  // 更新基础meta标签
  updateMetaTag('title', title)
  updateMetaTag('description', desc)
  
  // 更新微信专用标签 - 提前更新微信相关标签
  updateMetaTag('wechat:title', title, 'name')
  updateMetaTag('wechat:description', desc, 'name')
  updateMetaTag('wechat:image', absoluteImageUrl, 'name')
  updateMetaTag('wechat:card', 'summary_large_image', 'name')
  updateMetaTag('wechat:timeline_title', title, 'name')
  updateMetaTag('wechat:appmessage_title', title, 'name')
  updateMetaTag('wechat:appmessage_desc', desc, 'name')
  
  // 更新Open Graph标签
  updateMetaTag('og:title', title, 'property')
  updateMetaTag('og:description', desc, 'property')
  updateMetaTag('og:image', absoluteImageUrl, 'property')
  updateMetaTag('og:image:width', '300', 'property')
  updateMetaTag('og:image:height', '300', 'property')
  updateMetaTag('og:url', processedUrl, 'property')
  updateMetaTag('og:type', type, 'property')
  updateMetaTag('og:site_name', '蒙古丽人商城', 'property')
  updateMetaTag('og:locale', 'zh_CN', 'property')
  
  // 添加额外的微信分享标签
  updateMetaTag('wechat:share:title', title, 'name')
  updateMetaTag('wechat:share:description', desc, 'name')
  updateMetaTag('wechat:share:img', absoluteImageUrl, 'name')
  updateMetaTag('wechat:share:url', processedUrl, 'name')
  
  // 更新Twitter Card标签
  updateMetaTag('twitter:card', 'summary_large_image', 'name')
  updateMetaTag('twitter:title', title, 'name')
  updateMetaTag('twitter:description', desc, 'name')
  updateMetaTag('twitter:image', absoluteImageUrl, 'name')
  
  // 添加itemprop标签（微信也会读取这些标签）
  updateMetaTag('name', title, 'itemprop')
  updateMetaTag('description', desc, 'itemprop')
  updateMetaTag('image', absoluteImageUrl, 'itemprop')
  
  // 强制刷新meta标签
  forceMetaRefresh()
  
  console.log('Meta标签已更新:', { title, description: desc, image: absoluteImageUrl, url: processedUrl })
}

/**
 * 更新单个meta标签
 * @param {string} name 标签名称
 * @param {string} content 标签内容
 * @param {string} attribute 属性类型 ('name' 或 'property' 或 'itemprop')
 */
function updateMetaTag(name, content, attribute = 'name') {
  // 查找现有标签
  let metaTag
  
  if (attribute === 'itemprop') {
    metaTag = document.querySelector(`meta[itemprop="${name}"]`)
  } else {
    metaTag = document.querySelector(`meta[${attribute}="${name}"]`)
  }
  
  if (metaTag) {
    // 更新现有标签
    metaTag.setAttribute('content', content)
  } else {
    // 创建新标签
    metaTag = document.createElement('meta')
    if (attribute === 'itemprop') {
      metaTag.setAttribute('itemprop', name)
    } else {
      metaTag.setAttribute(attribute, name)
    }
    metaTag.setAttribute('content', content)
    document.head.appendChild(metaTag)
  }
}

/**
 * 强制刷新meta标签，确保微信能够读取到
 */
function forceMetaRefresh() {
  // 添加一个临时注释节点，强制浏览器重新计算DOM
  const tempComment = document.createComment('Force meta refresh for WeChat ' + new Date().getTime())
  document.head.appendChild(tempComment)
  
  // 延迟移除，确保DOM已更新
  setTimeout(() => {
    try {
      document.head.removeChild(tempComment)
    } catch (e) {
      console.warn('移除临时注释失败:', e)
    }
  }, 10)
}

/**
 * 更新页面标题
 * @param {string} title 新标题
 */
export function updatePageTitle(title) {
  document.title = title
  updateMetaTag('title', title)
  updateMetaTag('og:title', title, 'property')
  updateMetaTag('wechat:title', title, 'name')
}

/**
 * 更新分享图片
 * @param {string} imageUrl 图片URL
 */
export function updateShareImage(imageUrl) {
  // 确保图片URL是绝对路径
  const absoluteUrl = imageUrl.startsWith('http') ? imageUrl : `${window.location.origin}${imageUrl}`
  
  updateMetaTag('og:image', absoluteUrl, 'property')
  updateMetaTag('wechat:image', absoluteUrl, 'name')
}

/**
 * 更新分享描述
 * @param {string} description 描述内容
 */
export function updateShareDescription(description) {
  updateMetaTag('description', description)
  updateMetaTag('og:description', description, 'property')
  updateMetaTag('wechat:description', description, 'name')
}

/**
 * 更新分享URL
 * @param {string} url 页面URL
 */
export function updateShareUrl(url) {
  updateMetaTag('og:url', url, 'property')
}

/**
 * 设置商品页面的分享信息
 * @param {Object} product 商品信息
 */
export function setProductShareMeta(product) {
  const shareData = {
    title: `${product.name || '商品详情'} - 蒙古丽人商城`,
    description: product.description || product.name || '查看商品详情',
    image: product.image || product.cover || 'https://mall.sainbainu.com/static/images/sharelogo.png',
    url: window.location.href,
    type: 'product'
  }
  
  updateMetaTags(shareData)
}

/**
 * 设置订单页面的分享信息
 * @param {Object} order 订单信息
 */
export function setOrderShareMeta(order) {
  const shareData = {
    title: '我在蒙古丽人商城下单了',
    description: `订单金额：¥${order.amount || '0.00'}，快来看看吧！`,
    image: 'https://mall.sainbainu.com/static/images/sharelogo.png',
    url: window.location.href,
    type: 'order'
  }
  
  updateMetaTags(shareData)
}

/**
 * 设置新闻页面的分享信息
 * @param {Object} news 新闻信息
 */
export function setNewsShareMeta(news) {
  const shareData = {
    title: `${news.title || '新闻详情'} - 蒙古丽人商城`,
    description: news.summary || news.title || '查看新闻详情',
    image: news.image || 'https://mall.sainbainu.com/static/images/sharelogo.png',
    url: window.location.href,
    type: 'article'
  }
  
  updateMetaTags(shareData)
}

/**
 * 重置为默认分享信息
 */
export function resetToDefaultShareMeta() {
  const defaultData = {
    title: '蒙古丽人商城',
    description: '专注于蒙古服饰设计、开发、推广-',
    image: 'https://mall.sainbainu.com/static/images/sharelogo.png',
    url: window.location.href,
    type: 'website'
  }
  
  updateMetaTags(defaultData)
}

/**
 * 获取当前页面的分享信息
 * @returns {Object} 当前分享信息
 */
export function getCurrentShareMeta() {
  return {
    title: document.querySelector('meta[property="og:title"]')?.getAttribute('content') || document.title,
    description: document.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
    image: document.querySelector('meta[property="og:image"]')?.getAttribute('content') || '',
    url: document.querySelector('meta[property="og:url"]')?.getAttribute('content') || window.location.href,
    type: document.querySelector('meta[property="og:type"]')?.getAttribute('content') || 'website'
  }
}

export default {
  updateMetaTags,
  updatePageTitle,
  updateShareImage,
  updateShareDescription,
  updateShareUrl,
  setProductShareMeta,
  setOrderShareMeta,
  setNewsShareMeta,
  resetToDefaultShareMeta,
  getCurrentShareMeta
}
